import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import AgentStatusTrigger from './triggers/AgentStatusTrigger';
import {
  AgentStatusUpdateTrigger as AgentStatusUpdateTriggerType,
  DueInTrigger as DueInTriggerType,
  Trigger,
  TriggerType,
} from 'src/actions/rules/trigger';
import DueInTrigger from './triggers/DueInTrigger';
import { getGroupedMenuOptions } from './shared/getGroupedMenuOptions';
import RuleEditorSection, { StyledDropdown } from './RuleEditorSection';
import { StyledEntityRow } from './shared/StyledComponents';

type RuleTriggerProps = {
  trigger: Trigger;
  onSetTrigger: Function;
  onUpdateTrigger: Function;
};

enum TriggerGroup {
  Inbound = 'Inbound',
  Conversation = ' Conversation',
  Answer = 'Answer',
  Agent = 'Agent',
  Task = 'Task',
}

const RuleTrigger: FC<RuleTriggerProps> = ({ trigger, onSetTrigger, onUpdateTrigger }) => {
  const [triggerType, setTriggerType] = useState(trigger.type);

  const onChangeTriggerType = useCallback(
    type => {
      if (type === triggerType) return;
      setTriggerType(type);
      onSetTrigger(type);
    },
    [onSetTrigger, setTriggerType, triggerType]
  );

  const onChangeTrigger = useCallback(
    trigger => {
      onUpdateTrigger(trigger);
    },
    [onUpdateTrigger]
  );

  const groupedTriggerMenuOptions = getGroupedMenuOptions(triggerOptions, TriggerGroup);

  return (
    <RuleEditorSection title="Trigger">
      <StyledEntityRow>
        When
        <DropdownWrapper>
          <StyledTriggerDropdown
            attr="trigger"
            onSelect={onChangeTriggerType}
            options={groupedTriggerMenuOptions}
            value={triggerType}
          />
        </DropdownWrapper>
        <TriggerValue trigger={trigger} onChangeTrigger={onChangeTrigger} />
      </StyledEntityRow>
    </RuleEditorSection>
  );
};

const DropdownWrapper = styled.div`
  flex: 0 0 auto;
  margin-right: 12px;
`;

const StyledTriggerDropdown = styled(StyledDropdown)`
  width: 260px;
`;

type TriggerValueProps = {
  trigger: Trigger;
  onChangeTrigger: Function;
};

const TriggerValue: FC<TriggerValueProps> = ({ trigger, onChangeTrigger }) => {
  switch (trigger.type) {
    case TriggerType.AgentStatusUpdate:
      return <AgentStatusTrigger onChange={onChangeTrigger} trigger={trigger as AgentStatusUpdateTriggerType} />;
    case TriggerType.ConversationDueIn:
    case TriggerType.TaskDueIn:
      return <DueInTrigger onChange={onChangeTrigger} trigger={trigger as DueInTriggerType} />;
    default:
      return null;
  }
};

const triggerOptions: { label: string; value: string; group: string }[] = [
  { label: 'Agent status changes to', value: TriggerType.AgentStatusUpdate, group: TriggerGroup.Agent },
  { label: 'Answer used', value: TriggerType.AnswerUsed, group: TriggerGroup.Answer },
  { label: 'Assigned task due date is', value: TriggerType.TaskDueIn, group: TriggerGroup.Task },
  { label: 'Communication is received', value: TriggerType.IncomingCommunication, group: TriggerGroup.Inbound },
  { label: 'Conversation due date is', value: TriggerType.ConversationDueIn, group: TriggerGroup.Conversation },
  { label: 'Topics added', value: TriggerType.TopicsAdded, group: TriggerGroup.Conversation },
];

export default RuleTrigger;
